/**
 * nodiscount-warning.js  v2
 * ─────────────────────────────────────────────────────────────────
 * Shows a popup warning when a discount is applied to any cart item
 * tagged "nodiscount". Works with modern Shopify 2.0 themes that use
 * JavaScript-driven discount fields (no traditional form submission).
 *
 * INSTALLATION
 * ─────────────────────────────────────────────────────────────────
 * 1. Admin → Online Store → Themes → Edit Code
 * 2. Assets → "Add a new asset" → paste this file as nodiscount-warning.js
 * 3. In layout/theme.liquid, just before </body> add:
 *      {{ 'nodiscount-warning.js' | asset_url | script_tag }}
 * ─────────────────────────────────────────────────────────────────
 */

(function () {
  "use strict";

  /* ── 1. Inject styles ───────────────────────────────────────── */
  const css = `
    #nd-overlay {
      display: none;
      position: fixed;
      inset: 0;
      z-index: 999999;
      background: rgba(0,0,0,0.6);
      backdrop-filter: blur(4px);
      align-items: center;
      justify-content: center;
    }
    #nd-overlay.nd-open { display: flex; }

    #nd-box {
      background: #fff;
      border-radius: 12px;
      max-width: 430px;
      width: calc(100% - 32px);
      padding: 36px 30px 28px;
      box-shadow: 0 28px 70px rgba(0,0,0,0.22);
      text-align: center;
      animation: ndPop .22s cubic-bezier(.22,.68,0,1.15) both;
    }
    @keyframes ndPop {
      from { opacity:0; transform:scale(.93) translateY(12px); }
      to   { opacity:1; transform:scale(1)   translateY(0); }
    }

    #nd-icon {
      width:56px; height:56px; border-radius:50%;
      background:#fff3cd;
      display:flex; align-items:center; justify-content:center;
      margin: 0 auto 18px;
    }
    #nd-icon svg { width:30px; height:30px; }

    #nd-box h2 {
      margin:0 0 10px;
      font-size:1.15rem; font-weight:700; color:#111;
    }
    #nd-box p {
      font-size:.9rem; color:#555; line-height:1.65;
      margin:0 0 8px;
    }
    #nd-box strong { color:#c0392b; }

    #nd-item-list {
      background:#f8f8f8; border:1px solid #eee;
      border-radius:7px; padding:10px 14px;
      margin:14px 0 20px; text-align:left;
      font-size:.875rem; color:#333;
    }
    #nd-item-list ul { margin:4px 0 0; padding-left:18px; }
    #nd-item-list li { margin-bottom:3px; }

    #nd-btn-ok {
      display:block; width:100%; padding:13px;
      background:#111; color:#fff;
      border:none; border-radius:8px;
      font-size:.95rem; font-weight:600;
      cursor:pointer; transition:background .15s;
    }
    #nd-btn-ok:hover { background:#333; }

    #nd-btn-anyway {
      display:block; width:100%; margin-top:10px;
      padding:8px; background:none; border:none;
      color:#999; font-size:.82rem;
      text-decoration:underline; cursor:pointer;
    }
    #nd-btn-anyway:hover { color:#666; }
  `;
  const styleEl = document.createElement("style");
  styleEl.textContent = css;
  document.head.appendChild(styleEl);

  /* ── 2. Inject modal HTML ───────────────────────────────────── */
  const overlay = document.createElement("div");
  overlay.id = "nd-overlay";
  overlay.innerHTML = `
    <div id="nd-box" role="alertdialog" aria-modal="true" aria-labelledby="nd-title">
      <div id="nd-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="#b8860b" stroke-width="2.2"
             stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      <h2 id="nd-title">Discount Not Applicable</h2>
      <p>Your cart contains item(s) <strong>not eligible for discounts</strong>:</p>
      <div id="nd-item-list"><ul id="nd-items"></ul></div>
      <p>If a discount is applied to these items, your order will be
         <strong>cancelled and fully refunded</strong>.</p>
      <button id="nd-btn-ok">Got it — don't apply discount</button>
      <button id="nd-btn-anyway">I understand, apply anyway</button>
    </div>
  `;
  document.body.appendChild(overlay);

  /* ── 3. Modal helpers ───────────────────────────────────────── */
  function showModal(names) {
    document.getElementById("nd-items").innerHTML =
      names.map((n) => `<li>${esc(n)}</li>`).join("");
    overlay.classList.add("nd-open");
    document.getElementById("nd-btn-ok").focus();
  }
  function hideModal() { overlay.classList.remove("nd-open"); }

  function esc(s) {
    return String(s)
      .replace(/&/g,"&amp;").replace(/</g,"&lt;")
      .replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  }

  document.getElementById("nd-btn-ok").addEventListener("click", hideModal);
  document.getElementById("nd-btn-anyway").addEventListener("click", hideModal);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) hideModal(); });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && overlay.classList.contains("nd-open")) hideModal();
  });

  /* ── 4. Cart checker ────────────────────────────────────────── */
  async function getRestrictedItems() {
    try {
      const res = await fetch("/cart.js", { cache: "no-store" });
      if (!res.ok) return [];
      const cart = await res.json();
      return cart.items
        .filter((item) => {
          const tags = (item.product_tags || "")
            .split(",")
            .map((t) => t.trim().toLowerCase());
          return tags.includes("nodiscount");
        })
        .map((item) => item.product_title || item.title);
    } catch (e) {
      console.warn("[nodiscount]", e);
      return [];
    }
  }

  /* ── 5. Intercept Apply button clicks ───────────────────────── */
  function isApplyButton(el) {
    if (!el || el.tagName !== "BUTTON") return false;
    const txt = (el.textContent || el.value || el.getAttribute("aria-label") || "")
      .trim().toLowerCase();
    if (txt === "apply" || txt === "apply discount" || txt === "apply code") return true;

    // Also catch buttons with no text that sit next to a discount input
    const parent = el.closest("form, [class*='discount'], [id*='discount']");
    if (parent) {
      const inp = parent.querySelector(
        'input[name*="discount"], input[placeholder*="discount" i], input[id*="discount" i]'
      );
      if (inp) return true;
    }
    return false;
  }

  document.addEventListener(
    "click",
    async function (e) {
      let btn = e.target;
      for (let i = 0; i < 4; i++) {
        if (!btn) break;
        if (isApplyButton(btn)) {
          const container = btn.closest(
            "form, [class*='discount'], [id*='discount'], section, div"
          );
          const inp = container
            ? container.querySelector(
                'input[name*="discount"], input[placeholder*="discount" i], input[id*="discount" i]'
              )
            : null;

          const code = inp ? inp.value.trim() : "";
          if (!code) break;

          e.preventDefault();
          e.stopImmediatePropagation();

          const restricted = await getRestrictedItems();
          if (restricted.length > 0) {
            showModal(restricted);
          } else {
            btn.removeEventListener("click", arguments.callee);
            btn.click();
          }
          return;
        }
        btn = btn.parentElement;
      }
    },
    true
  );

  /* ── 6. Intercept fetch() ───────────────────────────────────── */
  const _fetch = window.fetch;
  window.fetch = async function (input, init) {
    const url = (typeof input === "string" ? input
      : input instanceof Request ? input.url
      : String(input)).split("?")[0];

    const isDiscountUrl = /\/discount\/[^/]+$/i.test(url);

    if (isDiscountUrl) {
      const restricted = await getRestrictedItems();
      if (restricted.length > 0) {
        showModal(restricted);
        return new Response(
          JSON.stringify({ blocked: true, reason: "nodiscount" }),
          { status: 200, headers: { "Content-Type": "application/json" } }
        );
      }
    }
    return _fetch.apply(this, arguments);
  };

  /* ── 7. Intercept XMLHttpRequest (legacy fallback) ──────────── */
  const _open = XMLHttpRequest.prototype.open;
  const _send = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._nd_url = String(url);
    return _open.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = async function (body) {
    if (/\/discount\/[^/]+$/i.test(this._nd_url || "")) {
      const restricted = await getRestrictedItems();
      if (restricted.length > 0) {
        showModal(restricted);
        this.abort();
        return;
      }
    }
    return _send.call(this, body);
  };

  /* ── 8. Intercept Enter key inside discount inputs ──────────── */
  document.addEventListener(
    "keydown",
    async function (e) {
      if (e.key !== "Enter") return;
      const el = e.target;
      const isDiscountInput =
        el.tagName === "INPUT" &&
        (/discount/i.test(el.name || "") ||
          /discount/i.test(el.id || "") ||
          /discount/i.test(el.placeholder || ""));
      if (!isDiscountInput || !el.value.trim()) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const restricted = await getRestrictedItems();
      if (restricted.length > 0) showModal(restricted);
    },
    true
  );

})();
